import { Figure, Hint, Line, Question } from '../components/Explain.tsx';
import { CoinIcon, compactUsd, tone } from '../components/ui.tsx';
import { MARKETS } from '../config.ts';
import { fmtUnits, fmtUsd, pct } from '../lib/format.ts';
import type { Globals, Market } from '../lib/protocol.ts';
import { gainShare } from '../../../offchain/src/math.ts';
import type { Activity } from '../lib/relayer.ts';
import { useApp } from '../state.tsx';

const dash = '- -';
const usd = (m: Market, base: bigint) => (m.mid ? (base * m.mid) / BigInt(m.inst.priceScale) : 0n);
/** Share of `part` in `whole`, as a percentage */
const share = (part: bigint, whole: bigint) => (whole > 0n ? Number((part * 100_000n) / whole) / 1000 : 0);
const fmtShare = (v: number) => `${v.toFixed(v >= 10 ? 0 : 1)}%`;
const signedUsd = (v: bigint) => `${v < 0n ? '-' : '+'}${compactUsd(v < 0n ? -v : v)}`;
const abs = (v: bigint) => (v < 0n ? -v : v);

/**
 * The house in plain words: a summary first (can it pay, how is it doing), then three questions a trader or a
 * staker actually asks, each answered with a few numbers and a hint on every term.
 */
export function HousePage() {
  const { globals, markets, stats, reserve } = useApp();
  const rows = (markets ?? []).map((m) => ({
    m,
    long: usd(m, m.oi.long),
    short: usd(m, m.oi.short),
    capL: usd(m, m.caps.long),
    capS: usd(m, m.caps.short),
  }));
  const totL = rows.reduce((a, r) => a + r.long, 0n);
  const totS = rows.reduce((a, r) => a + r.short, 0n);
  // the house holds the other side: traders net long means the house is net short
  const traderNet = totL - totS;
  // the house's directional risk as a share of what it holds: under 1% reads as balanced, a quarter is a warning
  const exposurePct = globals && globals.effectiveLp > 0n ? share(abs(traderNet), globals.effectiveLp) : undefined;
  const exposureTone = exposurePct === undefined ? (traderNet === 0n ? undefined : 'neg') : exposurePct >= 25 ? 'neg' : exposurePct >= 10 ? 'warn' : undefined;

  const lp = globals?.effectiveLp;
  const queued = globals?.totalQueued ?? 0n;
  const reserveSpot = reserve && globals ? reserve.spotWei * globals.coreScale : 0n;
  const reserveLent = reserve && globals ? reserve.suppliedWei * globals.coreScale : 0n;
  const reserveUsd = reserve && globals ? reserve.totalWei * globals.coreScale : undefined;
  const backing = lp !== undefined ? (lp > 0n ? lp : 0n) + (reserveUsd ?? 0n) : undefined;
  const day = stats?.activity24h;
  const houseDay = day ? -day.tradersPnl - day.fees : undefined;

  const status =
    lp === undefined
      ? undefined
      : lp < 0n
        ? { cls: 'neg', text: 'Owes more than it holds', detail: `Wins are queued until losses or the reserve refill it.` }
        : queued > 0n
          ? { cls: 'warn', text: 'Paying the queue', detail: `${compactUsd(queued)} of wins are waiting and get paid first, oldest first.` }
          : { cls: 'pos', text: 'Paying winners instantly', detail: 'No win is waiting: every winning close is paid on the spot.' };

  return (
    <div className="house-page">
      <section className="panel house-hero">
        <div className="house-hero-main">
          <div className="house-eyebrow">The House</div>
          <p className="house-intro">
            The house takes the other side of every trade: it pays winners from its liquidity and keeps what losers lose.
          </p>
          {status && (
            <div className={`house-status ${status.cls}`}>
              <i />
              <b>{status.text}</b>
              <span>{status.detail}</span>
            </div>
          )}
        </div>
        <div className="house-hero-figures">
          <Figure
            label="Ready to pay winners"
            hint="House liquidity minus the wins waiting in the queue and any lending deficit. This is what a winning close is paid from."
            value={lp !== undefined ? compactUsd(lp) : dash}
            tone={tone(lp)}
            sub={reserveUsd ? `+ ${compactUsd(reserveUsd)} reserve behind it` : undefined}
          />
          <Figure
            label="House result, 24h"
            hint="What traders lost minus what they won over the last 24 hours, after the fees that go to stakers and the protocol."
            value={houseDay !== undefined ? signedUsd(houseDay) : dash}
            tone={tone(houseDay)}
            sub={day ? `${compactUsd(day.volume)} traded in ${day.opened.toLocaleString()} trades` : undefined}
          />
        </div>
      </section>

      {globals && <ThresholdPanel g={globals} />}

      <div className="house-grid2">
        <Question
          title="Can the house pay winners?"
          lead="Winners are paid from the house. Behind it, a reserve on HyperCore tops it up when it runs low, and earns lending yield while it waits."
        >
          {globals && backing !== undefined && backing > 0n && (
            <StackBar
              parts={[
                { label: 'House', value: lp && lp > 0n ? lp : 0n, cls: 'house' },
                { label: 'Reserve, ready', value: reserveSpot, cls: 'spot' },
                { label: 'Reserve, earning', value: reserveLent, cls: 'lent' },
              ]}
              total={backing}
            />
          )}
          <Line
            label="House liquidity"
            hint="USDC the house holds to pay winners. Part of it can be lent on HyperCore's lending market: wins and losses settle in the house's books, so lent money only waits for a redeem before it can be withdrawn."
            value={globals ? `$${fmtUsd(globals.houseLiquidity, 0)}` : dash}
            sub={
              globals && globals.houseLent > 0n
                ? `$${fmtUsd(globals.houseLent, 0)} lent on HyperCore, up to ${Number(globals.houseLendMaxBps) / 100}% allowed`
                : globals && globals.houseLendMaxBps > 0n
                  ? `nothing lent, up to ${Number(globals.houseLendMaxBps) / 100}% allowed`
                  : undefined
            }
          />
          <Line
            label="Wins waiting"
            hint="Winning closes the house could not pay at once. They are paid first, oldest first, as the house refills."
            value={globals ? (queued > 0n ? `$${fmtUsd(queued, 0)}` : 'none') : dash}
            tone={queued > 0n ? 'neg' : undefined}
          />
          {reserve && globals ? (
            <>
              <Line
                label="Reserve on HyperCore"
                hint="Protocol capital that tops the house up when it owes more than it holds. Part stays ready, the rest is lent on HyperCore's lending market."
                value={`$${fmtUsd(reserveSpot + reserveLent, 0)}`}
                sub={`${fmtShare(share(reserve.suppliedWei, reserve.totalWei))} lent, at least $${fmtUsd(reserve.minLiquidWei * globals.coreScale, 0)} kept ready`}
              />
              <Line label="Total backing" value={backing !== undefined ? `$${fmtUsd(backing, 0)}` : dash} strong />
            </>
          ) : (
            <p className="card-sub">No reserve in this deployment: the house stands alone.</p>
          )}
        </Question>

        <Question
          title="What is the house exposed to?"
          lead="When longs and shorts balance, the house risks little: one side's losses pay the other side's wins. The risk is the difference."
        >
          <Diverging long={totL} short={totS} />
          <Line
            label="House position"
            hint="The opposite of the traders' net position: if traders are net long, the house loses when the price rises."
            value={
              !markets
                ? dash
                : traderNet === 0n || (exposurePct !== undefined && exposurePct < 1)
                  ? 'balanced'
                  : `${traderNet > 0n ? 'short' : 'long'} ${compactUsd(abs(traderNet))}`
            }
            tone={exposureTone}
            sub={
              exposurePct !== undefined && traderNet !== 0n
                ? exposurePct < 1
                  ? `${traderNet > 0n ? 'short' : 'long'} ${compactUsd(abs(traderNet))}, under 1% of its liquidity`
                  : `${fmtShare(exposurePct)} of its liquidity`
                : undefined
            }
          />
          {rows.map((r) => (
            <div key={r.m.id} className="room">
              <div className="room-head">
                <CoinIcon market={r.m.id} size={16} />
                <b>{MARKETS[r.m.id]?.symbol}</b>
                <span className="muted small">{r.m.inst.paused ? 'paused' : `up to ${compactUsd(r.m.inst.maxNotional)} per position`}</span>
                <Hint text="Each side can hold at most the other side plus half of the protocol's backing: the house liquidity plus the reserve, counting lent money only as far as the lending market could return it. A one-way market cannot put the house at risk beyond that. The bars show how much of that room is used." />
              </div>
              <Room label="Longs" value={r.long} cap={r.capL} cls="pos" />
              <Room label="Shorts" value={r.short} cap={r.capS} cls="neg" />
            </div>
          ))}
          <Line label="Open positions" value={stats ? stats.openPositions.toLocaleString() : dash} />
          <Line label="Traders, all time" value={stats ? stats.traders.toLocaleString() : dash} />
        </Question>

        <Question title="How is the house doing?" lead="Results are counted when positions close or are liquidated.">
          <table className="mini-table activity">
            <thead>
              <tr>
                <th />
                <th className="r">24h</th>
                <th className="r">All time</th>
              </tr>
            </thead>
            <tbody>
              <ActivityRow label="Volume" a={stats?.activity24h} b={stats?.activityAll} f={(x) => compactUsd(x.volume)} />
              <ActivityRow label="Trades closed" a={stats?.activity24h} b={stats?.activityAll} f={(x) => x.closed.toLocaleString()} />
              <ActivityRow label="Traders who won" a={stats?.activity24h} b={stats?.activityAll} f={(x) => (x.closed ? fmtShare((x.wins / x.closed) * 100) : dash)} />
              <ActivityRow label="Liquidations" a={stats?.activity24h} b={stats?.activityAll} f={(x) => x.liquidations.toLocaleString()} />
              <ActivityRow label="Traders' net result" a={stats?.activity24h} b={stats?.activityAll} f={(x) => signedUsd(x.tradersPnl)} tone={(x) => tone(x.tradersPnl)} />
              <ActivityRow label="Fees to stakers & protocol" a={stats?.activity24h} b={stats?.activityAll} f={(x) => compactUsd(x.fees)} />
              <ActivityRow
                label="House result"
                a={stats?.activity24h}
                b={stats?.activityAll}
                f={(x) => signedUsd(-x.tradersPnl - x.fees)}
                tone={(x) => tone(-x.tradersPnl - x.fees)}
                total
              />
            </tbody>
          </table>
        </Question>

        <Question
          title="Where do the fees go?"
          lead="Traders never pay a fee on their position size. The protocol and PAPURR stakers take a slice of outcomes."
        >
          <ul className="flow">
            <li>
              <b>On a win</b>
              <span>{globals ? `${pct(globals.winFeeRate)} of the profit: half to stakers, half to the protocol` : dash}</span>
            </li>
            <li>
              <b>On a loss</b>
              <span>
                {globals
                  ? `${pct(globals.rewardShares.loss)} to stakers and ${pct(globals.lossFeeRate)} to the protocol, the house keeps the rest (the stakers' share pauses while the house owes more than it holds); a liquidation splits the margin the same way`
                  : dash}
              </span>
            </li>
            <li>
              <b>Above {globals ? compactUsd(globals.rewardThreshold) : dash}</b>
              <span>
                {globals
                  ? `stakers also get ${pct(globals.rewardShares.newHigh)} of what the house gains past its record`
                  : dash}
              </span>
            </li>
          </ul>
          <Line
            label="PAPURR staked"
            hint="Staked PAPURR shares the HYPE stream, pro rata."
            value={globals ? fmtUnits(globals.totalStaked, 18, 0) : dash}
            sub={globals && globals.papurrSupply > 0n ? `${fmtShare(share(globals.totalStaked, globals.papurrSupply))} of ${fmtUnits(globals.papurrSupply, 18, 0)} supply` : undefined}
          />
          <Line
            label="Next distribution"
            hint="Rewards collected since the last distribution; the keeper sends them to the HYPE buyback at least hourly, and the HYPE bought is streamed to stakers."
            value={globals ? `$${fmtUsd(globals.pendingDistribution)}` : dash}
          />
          <a className="house-more" href="#/docs/learn/house-queue">
            How the house works →
          </a>
        </Question>
      </div>
    </div>
  );
}

/**
 * The house's funds against the reward threshold: how far it is, and once past it, which share of its gains goes to
 * stakers at the next distribution (none while it climbs back to its record, `newHigh` past the record)
 */
function ThresholdPanel({ g }: { g: Globals }) {
  const lp = g.effectiveLp;
  const t = g.rewardThreshold;
  const record = t + g.peakExcess;
  const above = lp > t;
  const pending = gainShare({
    effectiveLp: lp,
    threshold: t,
    peakExcess: g.peakExcess,
    newHighShare: g.rewardShares.newHigh,
  });
  // the bar runs a little past the furthest of the threshold, the funds and the record
  const far = [t + t / 4n, lp + lp / 10n, record + record / 20n].reduce((a, b) => (b > a ? b : a), 1n);
  const at = (v: bigint) => (v <= 0n ? 0 : Math.min(100, share(v, far)));
  const toGo = t > lp ? t - (lp > 0n ? lp : 0n) : 0n;

  const head =
    lp < 0n
      ? {
          cls: 'neg',
          title: 'The house owes more than it holds',
          text: `Stakers' ${pct(g.rewardShares.loss)} of losses is paused until the house is back above zero.`,
        }
      : !above
        ? {
            cls: '',
            title: `${compactUsd(toGo)} to go before stakers share the house's gains`,
            text: `${fmtShare(share(lp > 0n ? lp : 0n, t))} of the way to ${compactUsd(t)}. Until then stakers get ${pct(g.rewardShares.loss)} of every loss; past it, also ${pct(g.rewardShares.newHigh)} of every new record the house sets.`,
          }
        : lp < record
          ? {
              cls: 'pos',
              title: `Past the threshold, ${compactUsd(record - lp)} under the record`,
              text: `The house's gains are shared again once it passes its record of ${compactUsd(record)}: ${pct(g.rewardShares.newHigh)} of what it holds above it.`,
            }
          : {
              cls: 'pos',
              title: `New record: stakers get ${pct(g.rewardShares.newHigh)} of the house's gains`,
              text: `The house is at its highest above ${compactUsd(t)}: every new dollar it gains is shared at ${pct(g.rewardShares.newHigh)}.`,
            };

  return (
    <section className="panel threshold">
      <div className="threshold-head">
        <div className="house-eyebrow small">House funds vs. the reward threshold</div>
        <b className={head.cls}>{head.title}</b>
        <span>{head.text}</span>
      </div>
      <div className="th-bar" role="img" aria-label={`House funds ${compactUsd(lp)}, reward threshold ${compactUsd(t)}`}>
        <div className="th-track">
          {/* a sliver stays visible however far the threshold is */}
          <span className={`th-fill ${above ? 'past' : ''}`} style={{ width: `${lp > 0n ? Math.max(0.8, at(lp)) : 0}%` }} />
        </div>
        <span className="th-mark threshold-mark" style={{ left: `${at(t)}%` }}>
          <em>Threshold {compactUsd(t)}</em>
        </span>
        {g.peakExcess > 0n && (
          <span className="th-mark record-mark" style={{ left: `${at(record)}%` }}>
            <em>Record {compactUsd(record)}</em>
          </span>
        )}
      </div>
      <div className="threshold-figures">
        <Figure
          label="House funds"
          hint="The house's effective liquidity: what it holds minus the wins waiting in the queue and any lending deficit. This is what the threshold is measured against."
          value={compactUsd(lp)}
          tone={tone(lp)}
        />
        <Figure
          label="Reward threshold"
          hint="Above this level of house funds, stakers also share the house's new records at each distribution. Below it, they get their share of losses only."
          value={compactUsd(t)}
        />
        <Figure
          label="Above the record"
          hint="What the house holds above its record (the highest it was left at by a distribution, above the threshold). Only this is shared, once: a drop and the climb back to the record pay nothing."
          value={above && lp > record ? compactUsd(lp - record) : '$0'}
        />
        <Figure
          label="Stakers' share, next distribution"
          hint="The part of those gains the keeper sends to the HYPE buyback at the next distribution (at least hourly), on top of the stakers' share of losses and of the win fee."
          value={compactUsd(pending)}
          tone={pending > 0n ? 'pos' : undefined}
        />
      </div>
    </section>
  );
}

/** Shorts to the left, longs to the right, from a common center: the gap between the two is the house's risk */
function Diverging({ long, short }: { long: bigint; short: bigint }) {
  const max = long > short ? long : short;
  const w = (v: bigint) => (max > 0n ? Math.max(2, share(v, max)) : 0);
  return (
    <div className="diverging">
      <div className="diverging-side short">
        <span className="num">{compactUsd(short)}</span>
        <span className="diverging-track">
          <i className="neg" style={{ width: `${w(short)}%` }} />
        </span>
      </div>
      <div className="diverging-side long">
        <span className="diverging-track">
          <i className="pos" style={{ width: `${w(long)}%` }} />
        </span>
        <span className="num">{compactUsd(long)}</span>
      </div>
      <div className="diverging-legend">
        <span className="neg">Traders short</span>
        <span className="pos">Traders long</span>
      </div>
    </div>
  );
}

function StackBar({ parts, total }: { parts: { label: string; value: bigint; cls: string }[]; total: bigint }) {
  return (
    <div className="stack">
      <div className="stack-track">
        {parts.map((p) => (
          <span key={p.cls} className={`stack-${p.cls}`} style={{ width: `${share(p.value, total)}%` }} />
        ))}
      </div>
      <div className="stack-legend">
        {parts.map((p) => (
          <span key={p.cls}>
            <i className={`stack-${p.cls}`} /> {p.label} {compactUsd(p.value)}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActivityRow({
  label,
  a,
  b,
  f,
  tone: t,
  total,
}: {
  label: string;
  a?: Activity;
  b?: Activity;
  f: (x: Activity) => string;
  tone?: (x: Activity) => string;
  total?: boolean;
}) {
  return (
    <tr className={total ? 'total' : undefined}>
      <td className="muted">{label}</td>
      <td className={`r num ${a && t ? t(a) : ''}`}>{a ? f(a) : dash}</td>
      <td className={`r num ${b && t ? t(b) : ''}`}>{b ? f(b) : dash}</td>
    </tr>
  );
}

/** How much of a side's open-interest room is used */
function Room({ label, value, cap, cls }: { label: string; value: bigint; cap: bigint; cls: string }) {
  const used = cap > 0n ? Math.min(100, share(value, cap)) : 0;
  const left = cap > value ? cap - value : 0n;
  return (
    <div className="cap-bar">
      <span className={`small ${cls}`}>{label}</span>
      <span className="cap-track">
        <span className={`cap-fill ${cls}`} style={{ width: `${used}%` }} />
      </span>
      <span className="num small">
        {compactUsd(value)} <span className="muted">· {compactUsd(left)} room left</span>
      </span>
    </div>
  );
}
